import { Badge, Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Save,
  Print,
  Description,
  History,
  Preview,
  ViewAgenda,
  ViewWeek,
} from '@mui/icons-material';
import type { ReactNode } from 'react';

export type TestActionBarViewMode = 'single' | 'tabs';

export interface TestActionBarProps {
  /** Save handler. If `canSave` is false the segment is hidden. */
  onSave?: () => void;
  saving?: boolean;
  dirty?: boolean;
  canSave?: boolean;
  saveTooltip?: string;
  /** Hide the entire Save segment (e.g. when the active tab isn't editable). */
  saveHidden?: boolean;

  /** Print handler. Always rendered unless `canPrint` is false. */
  onPrint?: () => void;
  canPrint?: boolean;
  printTooltip?: string;

  /** Generate Report handler. Always rendered unless `canReport` is false. */
  onGenerateReport?: () => void;
  reportGenerating?: boolean;
  canReport?: boolean;
  reportTooltip?: string;

  /** Preview & Edit handler (report preview dialog). Rendered when provided,
   *  gated by the same `canReport` flag as Generate. */
  onPreviewReport?: () => void;
  previewTooltip?: string;

  /** Report Archive handler (standalone history dialog: archived renders +
   *  issued copies). Rendered when provided, gated by `canReport`. */
  onReportHistory?: () => void;
  historyTooltip?: string;

  /** Optional Single/Tabs view-mode segment. Pass undefined to omit. */
  viewMode?: TestActionBarViewMode;
  onViewModeChange?: (mode: TestActionBarViewMode) => void;

  /** Optional pager label, e.g. "3 / 16". Rendered as a separate chip outside the pill. */
  pagerLabel?: ReactNode;

  /** Optional small status node (e.g. "Generating report (40%)") shown before the pill. */
  leadingStatus?: ReactNode;
}

const SEGMENT_HEIGHT = 36;

/**
 * Unified "segmented pill" action bar used on every Test workflow page header
 * (Atterberg / PSD / Triaxial / DirectShear). Bundles Save + Print + Generate
 * Report + view-mode toggle into a single visually-integrated control so the
 * page header stops wrapping at common laptop widths and looks modern/sleek
 * instead of five disjoint buttons.
 *
 * Why a custom container instead of MUI ButtonGroup / SpeedDial:
 *  - ButtonGroup forces same variant per child; we need a maroon Save segment
 *    next to a primary Generate segment next to a borderless Print.
 *  - We want hairline dividers + gradient surface + global icon-only behavior,
 *    which neither primitive exposes cleanly.
 */
export const TestActionBar = ({
  onSave,
  saving = false,
  dirty = false,
  canSave = true,
  saveTooltip = 'Save Test',
  saveHidden = false,
  onPrint,
  canPrint = true,
  printTooltip = 'Print',
  onGenerateReport,
  reportGenerating = false,
  canReport = true,
  reportTooltip = 'Generate Report',
  onPreviewReport,
  previewTooltip = 'Preview & Edit Report',
  onReportHistory,
  historyTooltip = 'Report Archive (past renders & issued copies)',
  viewMode,
  onViewModeChange,
  pagerLabel,
  leadingStatus,
}: TestActionBarProps) => {
  const theme = useTheme();

  const showSave = !saveHidden && canSave && Boolean(onSave);
  const showPrint = canPrint && Boolean(onPrint);
  const showReport = canReport && Boolean(onGenerateReport);
  const showPreview = canReport && Boolean(onPreviewReport);
  const showHistory = canReport && Boolean(onReportHistory);
  const showViewToggle = Boolean(viewMode && onViewModeChange);

  const segments: Array<'save' | 'print' | 'history' | 'preview' | 'report' | 'view-single' | 'view-tabs'> = [];
  if (showSave) segments.push('save');
  if (showPrint) segments.push('print');
  if (showHistory) segments.push('history');
  if (showPreview) segments.push('preview');
  if (showReport) segments.push('report');
  if (showViewToggle) {
    segments.push('view-single');
    segments.push('view-tabs');
  }

  const isFirst = (key: typeof segments[number]) => segments[0] === key;
  const isLast = (key: typeof segments[number]) => segments[segments.length - 1] === key;
  const radiusFor = (key: typeof segments[number]) => {
    const left = isFirst(key) ? '999px' : '0';
    const right = isLast(key) ? '999px' : '0';
    return `${left} ${right} ${right} ${left}`;
  };

  const segmentBase = (key: typeof segments[number]) => ({
    height: SEGMENT_HEIGHT,
    minWidth: SEGMENT_HEIGHT,
    px: 1.25,
    borderRadius: radiusFor(key),
    // Hairline divider between segments — inset shadow so it sits inside
    // the container's rounded border without poking through the radius.
    boxShadow: isFirst(key) ? 'none' : `inset 1px 0 0 0 ${alpha(theme.palette.primary.main, 0.16)}`,
    color: theme.palette.text.primary,
    textTransform: 'none' as const,
    fontSize: '0.82rem',
    fontWeight: 600,
    gap: 0.6,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.16s ease, color 0.16s ease, box-shadow 0.2s ease',
    '&:disabled': { color: alpha(theme.palette.text.primary, 0.35) },
  });

  if (segments.length === 0 && !pagerLabel && !leadingStatus) return null;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.25,
        flexShrink: 0,
      }}
    >
      {leadingStatus && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{leadingStatus}</Box>
      )}

      {segments.length > 0 && (
        <Box
          // The unified pill container — gradient surface + hairline border +
          // soft outer shadow + inner highlight, so the segments visually fuse.
          sx={{
            display: 'inline-flex',
            alignItems: 'stretch',
            height: SEGMENT_HEIGHT,
            borderRadius: 999,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            background: `linear-gradient(135deg,
              ${alpha(theme.palette.primary.main, 0.05)} 0%,
              ${alpha('#FFFFFF', 0.7)} 48%,
              ${alpha(theme.customColors.maroon, 0.035)} 100%)`,
            boxShadow: `0 1px 3px ${alpha('#000', 0.04)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.75)}`,
            overflow: 'hidden',
          }}
          className="print-hide"
        >
          {/* ── Save segment (maroon-tinted) ────────────────────────────── */}
          {showSave && (
            <Tooltip title={dirty ? `${saveTooltip} (unsaved changes)` : saveTooltip}>
              <Badge
                variant="dot"
                invisible={!dirty || saving}
                color="warning"
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                  '& .MuiBadge-dot': {
                    width: 9,
                    height: 9,
                    minWidth: 9,
                    borderRadius: '50%',
                    border: `2px solid ${theme.palette.background.paper}`,
                    boxShadow: `0 0 0 1px ${alpha(theme.customColors.maroon, 0.55)}`,
                    top: 7,
                    right: 7,
                  },
                  // Wrap the segment, not the icon — so dot anchors to the segment edge.
                  display: 'inline-flex',
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={onSave}
                  disabled={saving}
                  aria-label={saveTooltip}
                  sx={{
                    ...segmentBase('save'),
                    border: 0,
                    backgroundColor: alpha(theme.customColors.maroon, 0.08),
                    color: theme.customColors.maroon,
                    cursor: saving ? 'default' : 'pointer',
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: alpha(theme.customColors.maroon, 0.16),
                    },
                  }}
                >
                  {saving
                    ? <CircularProgress size={15} sx={{ color: theme.customColors.maroon }} />
                    : <Save sx={{ fontSize: 18 }} />}
                  <Box
                    component="span"
                    sx={{
                      display: { xs: 'none', md: 'inline' },
                      fontWeight: 700,
                      letterSpacing: '0.01em',
                    }}
                  >
                    {saving ? 'Saving' : 'Save'}
                  </Box>
                </Box>
              </Badge>
            </Tooltip>
          )}

          {/* ── Print segment ──────────────────────────────────────────── */}
          {showPrint && (
            <Tooltip title={printTooltip}>
              <IconButton
                onClick={onPrint}
                disableRipple
                aria-label={printTooltip}
                sx={{
                  ...segmentBase('print'),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Print sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* ── Report Archive segment (history dialog) ────────────────── */}
          {showHistory && (
            <Tooltip title={historyTooltip}>
              <IconButton
                onClick={onReportHistory}
                disableRipple
                aria-label={historyTooltip}
                sx={{
                  ...segmentBase('history'),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <History sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* ── Preview & Edit segment (report preview dialog) ─────────── */}
          {showPreview && (
            <Tooltip title={previewTooltip}>
              <Box
                component="button"
                type="button"
                onClick={onPreviewReport}
                aria-label={previewTooltip}
                sx={{
                  ...segmentBase('preview'),
                  border: 0,
                  px: { xs: 1.25, sm: 1.5 },
                  backgroundColor: 'transparent',
                  color: theme.palette.primary.dark,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Preview sx={{ fontSize: 19 }} />
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', sm: 'inline' },
                    letterSpacing: '0.01em',
                  }}
                >
                  Preview
                </Box>
              </Box>
            </Tooltip>
          )}

          {/* ── Generate Report segment (primary CTA, soft glow) ───────── */}
          {showReport && (
            <Tooltip title={reportTooltip}>
              <Box
                component="button"
                type="button"
                onClick={onGenerateReport}
                disabled={reportGenerating}
                aria-label={reportTooltip}
                sx={{
                  ...segmentBase('report'),
                  border: 0,
                  px: { xs: 1.25, sm: 1.75 },
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.dark,
                  fontWeight: 700,
                  cursor: reportGenerating ? 'default' : 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.18),
                    boxShadow: `${isFirst('report') ? '' : `inset 1px 0 0 0 ${alpha(theme.palette.primary.main, 0.16)},`} 0 0 16px -2px ${alpha(theme.palette.primary.main, 0.45)}`,
                  },
                }}
              >
                {reportGenerating
                  ? <CircularProgress size={15} sx={{ color: theme.palette.primary.dark }} />
                  : <Description sx={{ fontSize: 19 }} />}
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', sm: 'inline' },
                    letterSpacing: '0.01em',
                  }}
                >
                  {reportGenerating ? 'Generating' : 'Generate'}
                </Box>
              </Box>
            </Tooltip>
          )}

          {/* ── View mode toggle (Single / Tabs) ──────────────────────── */}
          {showViewToggle && viewMode && onViewModeChange && (
            <>
              <Tooltip title="Single-page view">
                <IconButton
                  onClick={() => onViewModeChange('single')}
                  disableRipple
                  aria-label="Single-page view"
                  aria-pressed={viewMode === 'single'}
                  sx={{
                    ...segmentBase('view-single'),
                    backgroundColor: viewMode === 'single' ? alpha(theme.palette.primary.main, 0.14) : 'transparent',
                    color: viewMode === 'single' ? theme.palette.primary.dark : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: viewMode === 'single' ? alpha(theme.palette.primary.main, 0.18) : alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  <ViewAgenda sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Tabbed view">
                <IconButton
                  onClick={() => onViewModeChange('tabs')}
                  disableRipple
                  aria-label="Tabbed view"
                  aria-pressed={viewMode === 'tabs'}
                  sx={{
                    ...segmentBase('view-tabs'),
                    backgroundColor: viewMode === 'tabs' ? alpha(theme.palette.primary.main, 0.14) : 'transparent',
                    color: viewMode === 'tabs' ? theme.palette.primary.dark : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: viewMode === 'tabs' ? alpha(theme.palette.primary.main, 0.18) : alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  <ViewWeek sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )}

      {pagerLabel && (
        <Box
          className="print-hide"
          sx={{
            height: 26,
            minWidth: 46,
            px: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 999,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            color: theme.palette.text.secondary,
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
            {pagerLabel}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
